// "Your set" PDP picker state + multi-add (matching-set upsell).
// Contract with blocks/skullette-set-picker.liquid:
//   [data-set-picker] container - data-anchor-cents
//   [data-set-row] label rows with [data-set-tick] checkboxes
//   [data-set-island] JSON: { idx: { vid, cents, short, preB?, preE? } }
// Add flow (bẫy #1 note: this theme adds to cart from BUTTON CLICK, and the
// proven interception point is the click listener - same as the retired
// pair-addon): ticked members are batched through ONE /cart/add.js items[]
// call carrying _attach_source, THEN the native anchor submit runs so
// Horizon's own machinery opens and morphs the drawer showing every line.
// Errors fall back to untick-all + native submit - the shopper is never stuck.

const money = (cents) => {
  const base = `$${(cents / 100).toFixed(2)}`;
  return base.endsWith('.00') ? base.slice(0, -3) : base;
};
// Shopper-facing amount: the with-code price when the member sits in a
// campaign slot (island "cc"), else list. Cart math stays on list cents -
// the code is applied at checkout.
const payable = (m) => (Number.isFinite(m?.cc) ? m.cc : m?.cents ?? 0);

function initPicker(picker) {
  if (picker.__skSetInit) return;
  picker.__skSetInit = true;

  const island = picker.querySelector('[data-set-island]');
  let data = {};
  try {
    data = JSON.parse(island?.textContent || '{}');
  } catch {
    /* malformed island = behave as no members */
  }

  const rows = [...picker.querySelectorAll('[data-set-row]')];
  const totalWrap = picker.querySelector('[data-set-total]');
  const totalCount = picker.querySelector('[data-set-total-count]');
  const totalPrice = picker.querySelector('[data-set-total-price]');
  const totalWas = picker.querySelector('[data-set-total-was]');
  const totalCode = picker.querySelector('[data-set-total-code]');
  const section = picker.closest('section, .shopify-section') || document;
  const form = section.querySelector('product-form-component form');
  const atcButton = form?.querySelector('button[type="submit"][name="add"]');

  // idx rides along so downstream listeners (ship manifest) can find the
  // row's thumb without re-parsing the island
  const ticked = () =>
    rows
      .filter((r) => r.querySelector('[data-set-tick]')?.checked)
      .map((r) => {
        const idx = r.getAttribute('data-set-row');
        return data[idx] ? { ...data[idx], idx } : null;
      })
      .filter(Boolean);

  // ---- quick-view "Size & photos" sheet -------------------------------
  // One shell, one open member at a time. The sheet never adds to cart -
  // its ADD only ticks the member's checkbox and lets the picker's own
  // refresh path do everything else (totals, ATC attrs, set:change event).
  const qv = picker.querySelector('[data-set-qv]');
  let qvIdx = null;

  const shipsFor = (m) => {
    const anchorPre = picker.hasAttribute('data-anchor-pre');
    const anchorEta = (picker.getAttribute('data-anchor-eta') || '').replace(/^Ships\s+/, '');
    if (m.preB) return `${m.preE ? m.preE.replace(/^Ships\s+/, '') : 'With the next batch'} (pre-order)`;
    if (anchorPre) {
      const anchor = (picker.getAttribute('data-anchor-short') || 'piece').toLowerCase();
      return `Now · your ${anchor} follows ${anchorEta || 'the open batch'}`;
    }
    return 'Now';
  };

  function paintQvAdd() {
    const btn = qv?.querySelector('[data-set-qv-add]');
    if (!btn || qvIdx == null) return;
    const on = !!picker.querySelector(`[data-set-row="${qvIdx}"] [data-set-tick]`)?.checked;
    btn.classList.toggle('is-added', on);
    btn.textContent = on ? 'Added ✓' : `Add · ${money(payable(data[qvIdx]))}`;
  }

  const qvClose = () => {
    if (!qv || qv.hidden) return;
    qv.hidden = true;
    qvIdx = null;
    document.documentElement.classList.remove('sk-qv-lock');
  };

  const qvOpen = (idx) => {
    const m = data[idx];
    const tpl = picker.querySelector(`template[data-set-qv-tpl="${idx}"]`);
    const slot = qv?.querySelector('[data-set-qv-slot]');
    if (!m || !tpl || !slot) return;
    qvIdx = idx;
    slot.innerHTML = '';
    slot.appendChild(tpl.content.cloneNode(true));
    const price = slot.querySelector('[data-qv-price]');
    if (price) price.textContent = money(payable(m));
    const ships = slot.querySelector('[data-qv-ships]');
    if (ships) {
      ships.textContent = shipsFor(m);
      ships.hidden = false;
      const lbl = slot.querySelector('[data-qv-ships-label]');
      if (lbl) lbl.hidden = false;
    }
    paintQvAdd();
    qv.hidden = false;
    document.documentElement.classList.add('sk-qv-lock');
  };

  if (qv) {
    // openers sit INSIDE the <label> rows: preventDefault + stopPropagation
    // so opening the sheet can never toggle the row's checkbox
    picker.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-set-qv-open]');
      if (opener) {
        event.preventDefault();
        event.stopPropagation();
        qvOpen(opener.getAttribute('data-set-qv-open'));
        return;
      }
      if (event.target.closest('[data-set-qv-close]')) qvClose();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') qvClose();
    });
    qv.querySelector('[data-set-qv-add]')?.addEventListener('click', () => {
      if (qvIdx == null) return;
      const tick = picker.querySelector(`[data-set-row="${qvIdx}"] [data-set-tick]`);
      if (tick && !tick.checked) {
        tick.checked = true;
        tick.dispatchEvent(new Event('change', { bubbles: true }));
        paintQvAdd();
        // let the ✓ land before the sheet leaves - closing instantly reads
        // as "nothing happened"
        setTimeout(qvClose, 450);
      } else {
        qvClose();
      }
    });
  }

  const repaint = () => {
    const members = ticked();
    paintQvAdd();
    rows.forEach((r) => r.classList.toggle('is-on', !!r.querySelector('[data-set-tick]')?.checked));
    const anchorCents = parseInt(picker.getAttribute('data-anchor-cents'), 10) || 0;
    const anchorCc = parseInt(picker.getAttribute('data-anchor-cc'), 10);
    if (members.length >= 1 && totalWrap) {
      const sum = anchorCents + members.reduce((s, m) => s + m.cents, 0);
      // with-code total: list total struck beside it when a code applies;
      // the ATC button keeps the list sum, same as the solo headline price
      const codeSum =
        (Number.isNaN(anchorCc) ? anchorCents : anchorCc) + members.reduce((s, m) => s + payable(m), 0);
      const hasCode = codeSum !== sum;
      totalWrap.hidden = members.length < 1;
      if (totalCount) totalCount.textContent = String(members.length + 1);
      if (totalPrice) totalPrice.textContent = money(codeSum);
      if (totalWas) {
        totalWas.textContent = money(sum);
        totalWas.hidden = !hasCode;
      }
      if (totalCode) totalCode.hidden = !hasCode;
      atcButton?.setAttribute('data-set-base-cents', String(sum));
      atcButton?.setAttribute('data-set-payable-cents', String(sum));
    } else if (totalWrap) {
      totalWrap.hidden = true;
      atcButton?.removeAttribute('data-set-base-cents');
      atcButton?.removeAttribute('data-set-payable-cents');
    }
    document.dispatchEvent(
      new CustomEvent('skullette:set:change', {
        detail: { kind: members.length ? 'set' : 'solo', members },
      })
    );
  };

  rows.forEach((row) => {
    row.querySelector('[data-set-tick]')?.addEventListener('change', repaint);
  });

  picker.querySelector('[data-set-more]')?.addEventListener('click', () => {
    picker.classList.add('is-expanded');
    // inline display:none is the authoritative hiding - clear it here
    picker.querySelectorAll('.sk-set__row--more').forEach((r) => {
      r.style.display = '';
    });
  });

  const addMembers = async (members) => {
    const items = members.map((m) => {
      const properties = { _attach_source: 'set-picker' };
      // preorder members must carry the same promise labels as native adds
      if (m.preB) {
        properties._preorder = 'true';
        properties._preorder_batch = m.preB;
        if (m.preE) properties._preorder_eta = m.preE;
      }
      return { id: m.vid, quantity: 1, properties };
    });
    const res = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error(`cart/add ${res.status}`);
  };

  if (form && atcButton && !form.__skSetHooked) {
    form.__skSetHooked = true;
    let busy = false;

    const runSetAdd = async (viaSubmit) => {
      const members = ticked();
      if (!members.length || form.dataset.skSetHandled || busy) return false;
      busy = true;
      try {
        await addMembers(members);
      } catch (error) {
        console.error('set picker member add failed', error);
        rows.forEach((r) => {
          const tick = r.querySelector('[data-set-tick]');
          if (tick) tick.checked = false;
        });
        repaint();
      } finally {
        form.dataset.skSetHandled = 'true';
        if (!viaSubmit) form.requestSubmit();
        setTimeout(() => delete form.dataset.skSetHandled, 600);
        busy = false;
      }
      return true;
    };

    atcButton.addEventListener('click', async (event) => {
      const members = ticked();
      if (!members.length || form.dataset.skSetHandled || busy) return;
      event.preventDefault();
      await runSetAdd(false);
    });

    // Enter-key submits: members go in flight alongside the native add - the
    // drawer render may lag one update, cart contents stay correct.
    form.addEventListener('submit', () => {
      if (form.dataset.skSetHandled || busy) return;
      runSetAdd(true);
    });
  }

  repaint();
}

const scan = () => document.querySelectorAll('[data-set-picker]').forEach(initPicker);
scan();
// Variant switches morph the whole PDP section - re-init any picker the morph brings in.
new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
